import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getConfig } from '../../../config';
import type {
  ProductData,
  SelectedProductMap,
  CompetitorProduct,
} from '../../../pages/competitor/match-sku/models/types';
import type { MatchCompetitorResponse } from '../../../infrastructure/CompetitorClient';
import { CompetitorClient } from '../../../infrastructure/CompetitorClient';
import type { Competitor } from '../../../models/ProductTypes';
import { useNavigate, useLocation } from 'react-router-dom';

export const useMatchSku = () => {
  const config = getConfig();
  const competitorClient = new CompetitorClient(config.apiUrl);
  const navigate = useNavigate();
  const location = useLocation();

  // State definitions
  const [selectedProducts, setSelectedProducts] = useState<SelectedProductMap>({});
  const [selectedProduct, setSelectedProduct] = useState<ProductData | null>(null);
  const [activeCompetitor, setActiveCompetitor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterName, setFilterName] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Fetch data
  const fetchMachineMatchedProducts = async (): Promise<ProductData[]> => {
    try {
      return await competitorClient.fetchMachineMatchedProducts();
    } catch (error) {
      console.error('Failed to fetch machine matched products:', error);
      throw error;
    }
  };

  const matchedProductsQuery = useQuery({
    queryKey: ['matchedProducts'],
    queryFn: fetchMachineMatchedProducts,
    staleTime: 60000, // 1 minute
  });

  // Helper functions
  const countTotalMatches = (productData: ProductData | undefined): number => {
    if (!productData) {
      return 0;
    }
    return productData.machineMatchedProducts.reduce(
      (total, match) => total + (match.competitorProducts?.length || 0),
      0,
    );
  };

  const getFilteredProducts = (products: ProductData[], name: string, brand: string, category: string) =>
    products.filter((productData) => {
      const product = productData.skinSeoulProduct;

      const matchesName = !name || product.name.toLowerCase().includes(name.toLowerCase());

      const matchesBrand = !brand || (product.brand?.toLowerCase().includes(brand.toLowerCase()) ?? false);

      const matchesCategory =
        !category ||
        (product.category?.some(
          (cat) => typeof cat === 'string' && cat.toLowerCase().includes(category.toLowerCase()),
        ) ??
          false);

      return matchesName && matchesBrand && matchesCategory;
    });

  const findProductBySkuInList = (sku: string, products: ProductData[]): ProductData | undefined =>
    products.find((p) => p.skinSeoulProduct.sku === sku);

  const findFirstCompetitorWithProducts = (productData: ProductData): string | null => {
    const competitorWithProducts = productData.machineMatchedProducts.find(
      (comp) => comp.competitorProducts.length > 0,
    );
    return competitorWithProducts ? competitorWithProducts.competitor : null;
  };

  const findNextCompetitor = (productData: ProductData, currentCompetitor: string): string | null => {
    const currentIndex = productData.machineMatchedProducts.findIndex((comp) => comp.competitor === currentCompetitor);

    if (currentIndex >= 0) {
      for (let i = currentIndex + 1; i < productData.machineMatchedProducts.length; i++) {
        const competitor = productData.machineMatchedProducts[i];
        if (competitor && competitor.competitorProducts && competitor.competitorProducts.length > 0) {
          return competitor.competitor;
        }
      }
    }
    return null;
  };

  const findNextProductWithMatches = (currentProduct: ProductData, products: ProductData[]): ProductData | null => {
    if (!products || products.length === 0) {
      return null;
    }

    const currentIndex = products.findIndex((p) => p.skinSeoulProduct.sku === currentProduct.skinSeoulProduct.sku);

    if (currentIndex < 0 || currentIndex >= products.length - 1) {
      return null;
    }

    for (let i = currentIndex + 1; i < products.length; i++) {
      const product = products[i];
      if (product && countTotalMatches(product) > 0) {
        return product;
      }
    }

    return null;
  };

  const findNextProduct = () => {
    if (!selectedProduct) {
      return null;
    }

    const currentFilteredProducts = getFilteredProducts(
      matchedProductsQuery.data || [],
      filterName,
      filterBrand,
      filterCategory,
    );

    const currentIndex = currentFilteredProducts.findIndex(
      (p) => p.skinSeoulProduct.sku === selectedProduct.skinSeoulProduct.sku,
    );

    return currentIndex < currentFilteredProducts.length - 1 ? currentFilteredProducts[currentIndex + 1] : null;
  };

  // Core functions
  const selectCompetitorProduct = (
    skinSeoulSku: string,
    competitorName?: string,
    competitorSku?: string,
  ): Record<string, Record<string, string>> => {
    const updatedSelections: Record<string, Record<string, string>> = JSON.parse(JSON.stringify(selectedProducts));
    if (!updatedSelections[skinSeoulSku]) {
      updatedSelections[skinSeoulSku] = {};
    }

    if (competitorName) {
      updatedSelections[skinSeoulSku][competitorName] = competitorSku!;
    }

    setSelectedProducts(updatedSelections);

    return updatedSelections;
  };

  const saveAllSelectedProducts = async (
    skinSeoulSku: string,
    productSelections: Record<string, string>,
  ): Promise<MatchCompetitorResponse> => {
    try {
      const product = matchedProductsQuery.data?.find((p) => p.skinSeoulProduct.sku === skinSeoulSku);

      if (!product) {
        throw new Error(`Product with SKU ${skinSeoulSku} not found`);
      }

      const userSelections = productSelections;

      const machineMatchedData = product.machineMatchedProducts.map((match) => ({
        competitor: match.competitor as Competitor,
        competitorProductIdentifiers: match.competitorProducts.map((p) => ({
          sku: p.sku,
          possibility: p.possibility || 0,
        })),
      }));

      const knownCompetitors = product.machineMatchedProducts.map((m) => m.competitor);
      const humanMatchedProducts = knownCompetitors.map((competitorName) => {
        const competitor = competitorName as Competitor;
        const selectedSku = userSelections[competitor];

        return {
          competitor,
          competitorProductIdentifiers: selectedSku ? [{ sku: selectedSku, possibility: 1 }] : [],
        };
      });

      return await competitorClient.matchCompetitorProduct(skinSeoulSku, humanMatchedProducts, machineMatchedData);
    } catch (error) {
      console.error('Failed to save matched products:', error);
      throw error;
    }
  };

  const isProductSelected = (skinSeoulSku: string, competitor: string, competitorSku: string): boolean => {
    const result = Boolean(
      selectedProducts[skinSeoulSku] && selectedProducts[skinSeoulSku][competitor] === competitorSku,
    );

    console.log('isProductSelected:', { skinSeoulSku, competitor, competitorSku, selectedProducts, result });

    return result;
  };

  const selectSkinSeoulProduct = (skinSeoulProduct: ProductData) => {
    setSelectedProduct(skinSeoulProduct);

    const firstCompetitor = findFirstCompetitorWithProducts(skinSeoulProduct);
    setActiveCompetitor(firstCompetitor);

    if (!firstCompetitor) {
      setError("No matched product. Press 'q' to continue to next product.");
    } else {
      setError(null);
    }

    navigate(
      {
        pathname: location.pathname,
        search: `?sku=${skinSeoulProduct.skinSeoulProduct.sku}`,
      },
      { replace: true },
    );
  };

  const moveToNextSkinSeoulProduct = async () => {
    if (!selectedProduct) {
      return;
    }

    const nextProduct = findNextProduct();

    if (nextProduct) {
      selectSkinSeoulProduct(nextProduct);
    } else {
      setError("Congratulations! You've processed all products.");
    }
  };

  const saveAndMoveToNextProduct = async (currentSelections?: SelectedProductMap) => {
    if (!selectedProduct) {
      return;
    }

    try {
      const productSku = selectedProduct.skinSeoulProduct.sku;
      const selectionMap = (currentSelections || selectedProducts)[productSku] || {};
      await saveAllSelectedProducts(productSku, selectionMap);
      await moveToNextSkinSeoulProduct();
    } catch (error) {
      setError('Failed to save competitor matches. Please try again.');
      console.error('Failed to save matched products:', error);
    }
  };

  const moveToNextCompetitor = async (currentSelections?: SelectedProductMap) => {
    if (!selectedProduct) {
      return;
    }

    if (!activeCompetitor) {
      await saveAndMoveToNextProduct(currentSelections);
      return;
    }

    const nextCompetitor = findNextCompetitor(selectedProduct, activeCompetitor);

    if (nextCompetitor) {
      setActiveCompetitor(nextCompetitor);
      setError(null);
    } else {
      await saveAndMoveToNextProduct(currentSelections);
    }
  };

  const handleCompetitorProductSelect = async (product?: CompetitorProduct) => {
    if (!selectedProduct || !activeCompetitor) {
      return;
    }

    if (!product) {
      const updatedSelections = selectCompetitorProduct(selectedProduct.skinSeoulProduct.sku, activeCompetitor, '');
      await moveToNextCompetitor(updatedSelections);
      return;
    }

    try {
      const productSku = selectedProduct.skinSeoulProduct.sku;
      const updatedSelections = selectCompetitorProduct(productSku, activeCompetitor, product.sku);

      await moveToNextCompetitor(updatedSelections);
    } catch (error) {
      setError('Failed to select competitor product.');
      console.error('Error selecting competitor product:', error);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = async (e: KeyboardEvent) => {
      // Skip if in an input field
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (!selectedProduct) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'q':
          e.preventDefault();
          const updatedSelections = selectCompetitorProduct(
            selectedProduct.skinSeoulProduct.sku,
            activeCompetitor || undefined,
            '',
          );
          await moveToNextCompetitor(updatedSelections);
          break;
        case '1':
        case '2':
        case '3':
          e.preventDefault();
          if (activeCompetitor) {
            const keyIndex = parseInt(e.key) - 1;
            const competitorProducts = getCompetitorProductsForCurrentCompetitor();

            if (competitorProducts && competitorProducts.length > keyIndex) {
              const product = competitorProducts[keyIndex];
              await handleCompetitorProductSelect(product);
            }
          }
          break;
        default:
          break;
      }
    };

    // Helper function to get products for current competitor
    const getCompetitorProductsForCurrentCompetitor = (): CompetitorProduct[] => {
      if (!selectedProduct || !activeCompetitor) {
        return [];
      }

      const competitor = selectedProduct.machineMatchedProducts.find((comp) => comp.competitor === activeCompetitor);

      return competitor?.competitorProducts ?? [];
    };

    window.addEventListener('keydown', handleKeyPress);

    // For testing purposes, expose the handler
    (window as any).keydownHandler = handleKeyPress;

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      delete (window as any).keydownHandler;
    };
  }, [selectedProduct, activeCompetitor]);

  // Return hook API
  return {
    matchedProductsQuery,
    fetchMachineMatchedProducts,
    selectedProducts,
    selectedProduct,
    activeCompetitor,
    setActiveCompetitor,
    error,
    filterName,
    filterBrand,
    filterCategory,
    countTotalMatches,
    getFilteredProducts,
    findProductBySkuInList,
    findFirstCompetitorWithProducts,
    findNextCompetitor,
    findNextProductWithMatches,
    isProductSelected,
    selectCompetitorProduct,
    saveAllSelectedProducts,
    setFilterName,
    setFilterBrand,
    setFilterCategory,
    handleProductSelect: selectSkinSeoulProduct,
    findNextProduct,
    moveToNextSkinSeoulProduct,
    handleCompetitorProductSelect,
    moveToNextCompetitor,
  };
};
